import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Compass, Rocket, Search, Cloud, Network, GitBranch, Shield, FileCheck,
  Users, Layers, Activity, Gauge, Lock, Database, Server, Zap, Wrench,
  TrendingUp, AlertTriangle, Eye, BookOpen, BarChart3,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "product-discovery": {
    eyebrow: "Consultation · Strategy",
    title: "Product Discovery",
    sub: "Validate the right problem, audience and solution before you build.",
    metaDesc: "Product discovery sprints: customer interviews, opportunity scoring, and a validated roadmap before engineering starts.",
    intro:
      "We run focused discovery sprints — customer interviews, market scans, jobs-to-be-done framing, opportunity scoring — and deliver a validated roadmap your team can confidently execute against.",
    features: [
      { icon: Users, title: "Customer interviews", description: "10–15 conversations with target users and synthesis." },
      { icon: Compass, title: "JTBD framing", description: "Jobs, anxieties, and progress framing for every persona." },
      { icon: Search, title: "Market scan", description: "Competitor teardown and adjacent-category analysis." },
      { icon: TrendingUp, title: "Opportunity scoring", description: "RICE / Opportunity Score backlog with rationale." },
      { icon: BookOpen, title: "Validated roadmap", description: "Phased plan with milestones and success metrics." },
      { icon: Eye, title: "Decision-ready deck", description: "Findings packaged for execs, investors and the team." },
    ],
    process: [
      { step: "01", title: "Frame", description: "Hypotheses, research questions, and success criteria." },
      { step: "02", title: "Research", description: "Interviews, surveys, and competitor teardown." },
      { step: "03", title: "Synthesize", description: "Themes, opportunities, and scoring." },
      { step: "04", title: "Roadmap", description: "Phased plan with metrics and workshop handoff." },
    ],
    deliverables: ["Research synthesis", "Personas + JTBD map", "Competitor teardown", "Scored opportunity backlog", "Validated roadmap", "Executive deck"],
    pricing: [
      { name: "Sprint", price: "$8k", features: ["2-week discovery", "5–8 interviews", "Roadmap + deck", "1 workshop"] },
      { name: "Full discovery", price: "$22k", period: "project", highlighted: true, features: ["4-week engagement", "Mixed methods", "Roadmap + scoring", "Exec workshop"] },
      { name: "Embedded advisor", price: "$6k", period: "month", features: ["Weekly sessions", "Roadmap stewardship", "Continuous insight"] },
    ],
    faqs: [
      { q: "Do you recruit participants?", a: "Yes — screening, scheduling, and incentives are all handled by us." },
      { q: "B2B or B2C?", a: "Both, with appropriate methods and panel partners for each." },
    ],
  },
  "mvp-planning": {
    eyebrow: "Consultation · Strategy",
    title: "MVP Planning",
    sub: "Scope the smallest meaningful build that proves value and de-risks the bet.",
    metaDesc: "MVP planning services: scope, architecture, timeline and budget for a focused first launch.",
    intro:
      "Most MVPs fail by trying to do too much. We help you cut scope to the smallest meaningful version that proves value — with a realistic timeline, a clear architecture, and the success metrics that decide what comes next.",
    features: [
      { icon: Rocket, title: "Scope reduction", description: "Cut to the must-haves; defer everything else with confidence." },
      { icon: Compass, title: "Success metrics", description: "Define the numbers that say MVP succeeded — or didn't." },
      { icon: Layers, title: "Architecture sketch", description: "Stack, data model, and integrations sized to MVP scope." },
      { icon: TrendingUp, title: "Timeline + budget", description: "Phased plan with realistic estimates and risk flags." },
      { icon: Users, title: "Team shape", description: "Roles, hiring vs agency, and ownership map." },
      { icon: Wrench, title: "Build-vs-buy", description: "Where to ship custom vs use existing tools/APIs." },
    ],
    process: [
      { step: "01", title: "Goals", description: "What success looks like and what we'll measure." },
      { step: "02", title: "Cut scope", description: "Workshop to ruthlessly reduce to MVP." },
      { step: "03", title: "Plan", description: "Architecture, timeline, budget, and team shape." },
      { step: "04", title: "Handoff", description: "Plan walkthrough with engineering or agency partners." },
    ],
    deliverables: ["MVP scope doc", "Success metric definitions", "Architecture sketch", "Timeline + budget", "Team + ownership map", "Build-vs-buy decisions"],
    pricing: [
      { name: "Workshop", price: "$3k", features: ["2-day intensive", "Scope + metrics", "Plan summary"] },
      { name: "Full plan", price: "$10k", period: "project", highlighted: true, features: ["2-week engagement", "Architecture + budget", "Workshop + handoff"] },
      { name: "Advisor", price: "$4k", period: "month", features: ["Weekly check-ins", "Plan stewardship", "Vendor support"] },
    ],
    faqs: [
      { q: "Do you build it after?", a: "Optional. We design the plan to work with any engineering team." },
      { q: "What's a realistic MVP timeline?", a: "8–12 weeks for most software MVPs. We flag when expectations don't match." },
    ],
  },
  "tech-stack-audit": {
    eyebrow: "Consultation · Strategy",
    title: "Tech Stack Audit",
    sub: "Independent review of your stack, debt, and scaling path.",
    metaDesc: "Independent tech stack audit covering architecture, code quality, security, performance and scaling roadmap.",
    intro:
      "We give you an honest, vendor-neutral read on your stack — what's solid, what's debt, what'll break under growth, and the prioritized changes that actually move the needle.",
    features: [
      { icon: Search, title: "Code + arch review", description: "Repository walk-through, patterns, and debt mapping." },
      { icon: Gauge, title: "Performance review", description: "Bottlenecks across frontend, backend and database." },
      { icon: Shield, title: "Security review", description: "OWASP top 10, authn/z, secrets, and dependency CVEs." },
      { icon: BarChart3, title: "Cost analysis", description: "Cloud + SaaS spend with concrete reduction levers." },
      { icon: TrendingUp, title: "Scaling roadmap", description: "What changes when traffic, team, or data 10×?" },
      { icon: BookOpen, title: "Findings report", description: "Severity-scored issues with effort + impact estimates." },
    ],
    process: [
      { step: "01", title: "Scope", description: "Repos, surfaces, and key concerns from leadership." },
      { step: "02", title: "Review", description: "Code, infra, and stakeholder interviews." },
      { step: "03", title: "Synthesize", description: "Findings, severity, and prioritization." },
      { step: "04", title: "Workshop", description: "Live walkthrough with engineering and execs." },
    ],
    deliverables: ["Findings report", "Severity-scored backlog", "Scaling roadmap", "Cost reduction plan", "Architecture diagrams", "Live walkthrough"],
    pricing: [
      { name: "Express", price: "$4k", features: ["1-week audit", "Top 10 findings", "1 readout"] },
      { name: "Full audit", price: "$12k", period: "project", highlighted: true, features: ["2-week deep dive", "Backlog + roadmap", "Cost + scaling plan", "Workshop"] },
      { name: "Audit + execute", price: "$25k", period: "project", features: ["Audit", "Top fixes shipped", "Follow-up review"] },
    ],
    faqs: [
      { q: "Will you favor your preferred stack?", a: "No — recommendations are vendor-neutral and tied to your context." },
      { q: "Pre-acquisition due diligence?", a: "Yes — we do tech DD for VC/PE with confidential reporting." },
    ],
  },
  "cloud-architecture": {
    eyebrow: "Consultation · Architecture",
    title: "Cloud Architecture",
    sub: "Design and review cloud architectures on AWS, GCP, Azure or edge runtimes.",
    metaDesc: "Cloud architecture services for AWS, GCP, Azure and edge: design, review, cost optimization and migration plans.",
    intro:
      "We design cloud architectures that are right-sized for today and ready for 10× tomorrow — multi-region where it matters, edge where it pays off, single-region where it should stay. Independent of any cloud vendor, focused on what actually fits.",
    features: [
      { icon: Cloud, title: "Multi-cloud + edge", description: "AWS, GCP, Azure, Cloudflare and Vercel architectures." },
      { icon: Layers, title: "Reference architectures", description: "Diagrams, IaC modules, and well-architected reviews." },
      { icon: Gauge, title: "Cost optimization", description: "Right-sizing, savings plans, and waste elimination." },
      { icon: Shield, title: "Security baseline", description: "VPC design, secrets, IAM, and zero-trust patterns." },
      { icon: Activity, title: "Observability", description: "Logs, metrics, traces, and SLO design." },
      { icon: TrendingUp, title: "Scaling playbook", description: "Plan for 10× traffic, data and team growth." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Current architecture, bills, incidents, and pain points." },
      { step: "02", title: "Design", description: "Target architecture with trade-offs and ADRs." },
      { step: "03", title: "Plan", description: "Phased migration with risk and rollback strategy." },
      { step: "04", title: "Execute", description: "Implementation support and IaC modules." },
    ],
    deliverables: ["Current + target diagrams", "Architecture decision records", "IaC modules (Terraform/Pulumi)", "Cost optimization plan", "Observability blueprint", "Migration playbook"],
    techStack: ["AWS", "GCP", "Azure", "Cloudflare", "Vercel", "Terraform", "Pulumi", "Kubernetes", "Datadog"],
    pricing: [
      { name: "Review", price: "$5k", features: ["1-week audit", "Findings + diagrams", "1 workshop"] },
      { name: "Design + plan", price: "$18k", period: "project", highlighted: true, features: ["Target arch + ADRs", "IaC modules", "Migration plan"] },
      { name: "Embedded", price: "$10k", period: "month", features: ["Fractional cloud architect", "IaC reviews", "On-call escalations"] },
    ],
    faqs: [
      { q: "Which cloud should we pick?", a: "Depends on team skills, customer geography, and partner ecosystem — not on hype." },
      { q: "Will you write Terraform?", a: "Yes — modules, pipelines, and review of existing IaC." },
    ],
  },
  "microservices": {
    eyebrow: "Consultation · Architecture",
    title: "Microservices",
    sub: "Decompose monoliths, design service boundaries, and avoid distributed-system pain.",
    metaDesc: "Microservices consulting: decomposition strategy, service boundaries, event-driven architecture and migration planning.",
    intro:
      "Microservices are powerful and dangerous. We help you decide when to split, where the right boundaries are, how to handle data, events and contracts — and when staying with a well-modularized monolith is actually the right call.",
    features: [
      { icon: Network, title: "Decomposition strategy", description: "Domain-driven split lines and migration sequencing." },
      { icon: Layers, title: "Service boundaries", description: "Contracts, ownership, and team alignment (Conway's law)." },
      { icon: Database, title: "Data strategy", description: "Per-service stores, CDC, and shared data avoidance." },
      { icon: Zap, title: "Event-driven", description: "Kafka, NATS, or SNS/SQS patterns done right." },
      { icon: Activity, title: "Service mesh + obs", description: "Tracing, retries, circuit breakers, and SLOs." },
      { icon: GitBranch, title: "Migration plan", description: "Strangler fig with risk-bounded rollouts." },
    ],
    process: [
      { step: "01", title: "Assess", description: "Monolith analysis, team topology, scaling pain." },
      { step: "02", title: "Design", description: "Bounded contexts, events, and contracts." },
      { step: "03", title: "Plan", description: "Strangler migration with risk flags." },
      { step: "04", title: "Execute", description: "Pilot service rollout and patterns." },
    ],
    deliverables: ["Domain map + bounded contexts", "Service catalog + contracts", "Event/messaging blueprint", "Data ownership plan", "Strangler migration plan", "Pilot service implementation"],
    techStack: ["gRPC", "GraphQL Federation", "Kafka", "NATS", "Istio", "Linkerd", "OpenTelemetry", "Kubernetes"],
    pricing: [
      { name: "Assessment", price: "$6k", features: ["1-week audit", "Decomposition map", "Recommendations"] },
      { name: "Design + pilot", price: "$25k", period: "project", highlighted: true, features: ["Full design", "Pilot service", "Migration plan"] },
      { name: "Embedded", price: "$12k", period: "month", features: ["Fractional architect", "Service reviews", "Ongoing rollout support"] },
    ],
    faqs: [
      { q: "Should we always split?", a: "No. A modular monolith often beats premature microservices. We'll tell you." },
      { q: "Kafka or simpler?", a: "Depends on throughput, team experience, and operational appetite." },
    ],
  },
  "devops-cicd": {
    eyebrow: "Consultation · Architecture",
    title: "DevOps & CI/CD",
    sub: "Reliable pipelines, faster releases, and the platform engineering to make it stick.",
    metaDesc: "DevOps and CI/CD consulting: pipelines, GitOps, observability, SRE practices and platform engineering.",
    intro:
      "We set up the pipelines and platform engineering that let your team ship safely and often — GitOps deployments, preview environments, automated tests, observability, on-call rituals, and the cultural changes that make DORA metrics actually move.",
    features: [
      { icon: GitBranch, title: "CI/CD pipelines", description: "Per-branch previews, parallel tests, blue/green deploys." },
      { icon: Cloud, title: "GitOps", description: "ArgoCD/Flux with declarative environments and rollbacks." },
      { icon: Activity, title: "Observability", description: "Logs, metrics, traces, SLOs, and useful dashboards." },
      { icon: Shield, title: "Secrets + supply chain", description: "Vault, SOPS, SBOM, and signed artifacts." },
      { icon: AlertTriangle, title: "Incident response", description: "Runbooks, on-call rituals, and blameless postmortems." },
      { icon: Wrench, title: "Platform engineering", description: "Internal developer platform and golden paths." },
    ],
    process: [
      { step: "01", title: "Assess", description: "DORA metrics, pipeline review, and pain points." },
      { step: "02", title: "Design", description: "Target pipelines, GitOps, and observability stack." },
      { step: "03", title: "Build", description: "Implement pipelines and platform components." },
      { step: "04", title: "Adopt", description: "Training, runbooks, and adoption tracking." },
    ],
    deliverables: ["DORA baseline + targets", "CI/CD pipelines + IaC", "GitOps environment setup", "Observability dashboards", "Runbooks + on-call rituals", "Team training"],
    techStack: ["GitHub Actions", "GitLab CI", "ArgoCD", "Flux", "Terraform", "Datadog", "Grafana", "PagerDuty"],
    pricing: [
      { name: "Audit", price: "$4k", features: ["1-week assessment", "Findings + roadmap", "Workshop"] },
      { name: "Build", price: "$22k", period: "project", highlighted: true, features: ["Pipelines + GitOps", "Observability", "Runbooks", "Training"] },
      { name: "Platform retainer", price: "$8k", period: "month", features: ["Fractional platform engineer", "Continuous improvements", "On-call support"] },
    ],
    faqs: [
      { q: "Do you do Kubernetes?", a: "Yes — when it makes sense. Often Cloud Run, Fly, or ECS is simpler and cheaper." },
      { q: "Can you set up SRE rituals?", a: "Yes — SLOs, error budgets, on-call rotations, and blameless postmortems." },
    ],
  },
  "security-audit": {
    eyebrow: "Consultation · Security",
    title: "Security Audit",
    sub: "Find what's exploitable, fix what matters, and prove you did.",
    metaDesc: "Application and infrastructure security audits with OWASP-aligned findings, remediation and re-test.",
    intro:
      "We audit your app and infrastructure the way an attacker would — authentication, authorization, secrets, dependencies, cloud config — then deliver prioritized findings with concrete fixes and a re-test to prove they worked.",
    features: [
      { icon: Eye, title: "App pen test", description: "OWASP Top 10, business logic, authn/z, and APIs." },
      { icon: Cloud, title: "Cloud config review", description: "IAM, networking, secrets, and CIS benchmark scoring." },
      { icon: Lock, title: "Dependency + supply chain", description: "CVEs, SBOM, and build pipeline security." },
      { icon: Database, title: "Data security", description: "Encryption, PII handling, backups, and RLS review." },
      { icon: AlertTriangle, title: "Severity scoring", description: "CVSS scoring with realistic exploitability." },
      { icon: FileCheck, title: "Remediation + re-test", description: "Fix guidance and free re-test after remediation." },
    ],
    process: [
      { step: "01", title: "Scope", description: "Surfaces, assets, and rules of engagement." },
      { step: "02", title: "Test", description: "Manual + automated testing across surfaces." },
      { step: "03", title: "Report", description: "CVSS-scored findings with exploitation details." },
      { step: "04", title: "Re-test", description: "Verify fixes and update report." },
    ],
    deliverables: ["Audit scope + ROE doc", "Findings report (CVSS-scored)", "Proof-of-concept exploits", "Remediation playbook", "Re-test report", "Executive summary"],
    techStack: ["Burp Suite", "OWASP ZAP", "Semgrep", "Trivy", "Prowler", "ScoutSuite"],
    pricing: [
      { name: "Express", price: "$5k", features: ["Light app pen test", "Top findings", "Remediation guide"] },
      { name: "Full audit", price: "$15k", period: "project", highlighted: true, features: ["App + cloud", "Manual + automated", "Re-test included"] },
      { name: "Continuous", price: "$6k", period: "month", features: ["Quarterly tests", "Dependency monitoring", "On-demand reviews"] },
    ],
    faqs: [
      { q: "Will you do a pen test or just a scan?", a: "Manual testing with experienced engineers — scans complement, not replace." },
      { q: "Do you re-test fixes?", a: "Yes — re-test is included in Full Audit pricing." },
    ],
  },
  "compliance-gdpr": {
    eyebrow: "Consultation · Security",
    title: "Compliance (GDPR & beyond)",
    sub: "GDPR, CCPA, SOC 2, HIPAA — get audit-ready without grinding to a halt.",
    metaDesc: "Compliance consulting for GDPR, CCPA, SOC 2 and HIPAA — gap analysis, controls, evidence and audit prep.",
    intro:
      "We help SaaS and product teams get audit-ready for GDPR, CCPA, SOC 2 and HIPAA — gap analysis, control implementation, evidence collection, vendor reviews, and DPO/CISO advisory support through the audit itself.",
    features: [
      { icon: FileCheck, title: "Gap analysis", description: "Current state vs target framework with prioritized gaps." },
      { icon: Shield, title: "Controls implementation", description: "Policies, technical controls, and evidence pipelines." },
      { icon: Lock, title: "DPIA + ROPA", description: "Data flows, processing records, and impact assessments." },
      { icon: Users, title: "Vendor reviews", description: "DPA tracking, sub-processor assessments, and risk scoring." },
      { icon: AlertTriangle, title: "Breach readiness", description: "Incident response plans aligned with 72-hour rules." },
      { icon: BookOpen, title: "Audit support", description: "Auditor-facing evidence and walkthrough rehearsals." },
    ],
    process: [
      { step: "01", title: "Assess", description: "Framework scoping and gap analysis." },
      { step: "02", title: "Implement", description: "Policies, controls, and evidence pipelines." },
      { step: "03", title: "Operate", description: "Run controls for required observation period." },
      { step: "04", title: "Audit", description: "Auditor coordination and walkthrough support." },
    ],
    deliverables: ["Gap analysis report", "Policy + control library", "DPIA + ROPA documents", "Vendor risk register", "Evidence pipeline", "Auditor coordination"],
    techStack: ["Vanta", "Drata", "Secureframe", "TrustCloud", "OneTrust"],
    pricing: [
      { name: "Gap analysis", price: "$5k", features: ["1-week assessment", "Gap report", "Roadmap"] },
      { name: "Audit prep", price: "$25k", period: "project", highlighted: true, features: ["Controls + evidence", "Policies + DPIA", "Audit coordination"] },
      { name: "Embedded DPO/CISO", price: "$8k", period: "month", features: ["Fractional advisor", "Continuous compliance", "Vendor + breach support"] },
    ],
    faqs: [
      { q: "Which framework first?", a: "Usually GDPR for EU exposure or SOC 2 for enterprise sales — we'll advise based on your buyers." },
      { q: "How long to SOC 2?", a: "Type I in ~3 months, Type II adds the 3–6 month observation window." },
    ],
  },
};

export const Route = createFileRoute("/services/tech-consultation/$child")({
  component: ChildPage,
  loader: ({ params }) => {
    const entry = CHILDREN[params.child];
    if (!entry) throw notFound();
    return { entry };
  },
  head: ({ params }) => {
    const entry = CHILDREN[params.child];
    if (!entry) return {};
    const url = `/services/tech-consultation/${params.child}`;
    return {
      meta: [
        { title: `${entry.title} — CoderLity` },
        { name: "description", content: entry.metaDesc },
        { property: "og:title", content: `${entry.title} — CoderLity` },
        { property: "og:description", content: entry.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
});

function ChildPage() {
  const { entry } = Route.useLoaderData();
  return <ServicePage data={entry} />;
}
